import type { HeightfieldData } from './geometry.js';

/**
 * Bilinear height lookup. `x` and `z` are in world meters relative to the chunk origin
 * (the same frame as terrain positions). Returns 0 outside the chunk.
 */
export function sampleHeightfield(h: HeightfieldData, x: number, z: number): number {
  const half = h.sizeMeters * 0.5;
  const u = (x + half) / h.sizeMeters;
  const v = (z + half) / h.sizeMeters;
  if (u < 0 || u > 1 || v < 0 || v > 1) return 0;
  const fx = u * (h.resolution - 1);
  const fy = v * (h.resolution - 1);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, h.resolution - 1);
  const y1 = Math.min(y0 + 1, h.resolution - 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const idx = (y: number, x: number) => y * h.resolution + x;
  const a = (h.heights[idx(y0, x0)] ?? 0) * (1 - tx) + (h.heights[idx(y0, x1)] ?? 0) * tx;
  const b = (h.heights[idx(y1, x0)] ?? 0) * (1 - tx) + (h.heights[idx(y1, x1)] ?? 0) * tx;
  return a * (1 - ty) + b * ty;
}
